export type OrganizationKey = 'spectrum_reach' | 'gemmetric';

export const DEFAULT_ORGANIZATION: OrganizationKey = 'spectrum_reach';

export const ORGANIZATION_LABELS: Record<OrganizationKey, string> = {
  spectrum_reach: 'Spectrum Reach',
  gemmetric: 'Gemmetric'
};

export const ORGANIZATION_SCORE_LABELS: Record<OrganizationKey, string> = {
  spectrum_reach: 'Fit Score',
  gemmetric: 'Candidate Score'
};

export const ORGANIZATION_ANALYSIS_TITLES: Record<OrganizationKey, string> = {
  spectrum_reach: 'AI Analysis',
  gemmetric: 'AI Visibility Candidate'
};

export const ORGANIZATION_RECOMMENDATIONS_LABELS: Record<OrganizationKey, string> = {
  spectrum_reach: 'Recommended Ad Solutions',
  gemmetric: 'Likely Opportunity Areas'
};

export const ORGANIZATION_REASONING_LABELS: Record<OrganizationKey, string> = {
  spectrum_reach: 'Why this is a good prospect',
  gemmetric: 'Why this may need AI Visibility Infrastructure'
};

export type GemmetricOpportunityLevel = 'low' | 'moderate' | 'high' | 'significant';

export type GemmetricFindingConfidence = 'observed' | 'strong' | 'heuristic';

export interface GemmetricLiteScanFinding {
  id: string;
  label: string;
  status: 'pass' | 'warn' | 'fail' | 'unknown';
  detail?: string;
  businessLabel?: string;
  whyItMatters?: string;
  /** How the finding was derived: observed fact, strong indicator, or heuristic observation */
  confidence?: GemmetricFindingConfidence;
}

export interface GemmetricLiteScanSnapshot {
  strengths: string[];
  opportunities: string[];
}

export interface GemmetricLiteScanResult {
  websiteUrl: string;
  scannedAt: string;
  reachable: boolean;
  opportunityScore?: number;
  /** @deprecated Use opportunityLevel */
  visibilityRisk?: 'low' | 'medium' | 'high';
  opportunityLevel?: GemmetricOpportunityLevel;
  snapshot?: GemmetricLiteScanSnapshot;
  findings: GemmetricLiteScanFinding[];
  warnings: string[];
  observations: string[];
  /** Lightweight prospecting observations — not audit conclusions */
  prospectingInsights?: string[];
  summary?: string;
  error?: string;
}

export interface AnalyzeOptions {
  orgKey?: OrganizationKey;
}

export interface StrategyOptions {
  orgKey?: OrganizationKey;
  liteScan?: GemmetricLiteScanResult | null;
}

export const isValidOrganizationKey = (value: string | null | undefined): value is OrganizationKey => {
  return value === 'spectrum_reach' || value === 'gemmetric';
};

export const organizationStorageKey = (userId: string): string => `raedar_org_${userId}`;

export const resolveOrgKey = (value: string | null | undefined): OrganizationKey => {
  return isValidOrganizationKey(value) ? value : DEFAULT_ORGANIZATION;
};

export const operatingModeForOrganization = (orgKey: OrganizationKey): 'gemmetric' | 'spectrum-reach' =>
  orgKey === 'gemmetric' ? 'gemmetric' : 'spectrum-reach';

export const recordMatchesOrg = (
  record: { orgKey?: string } | null | undefined,
  orgKey: OrganizationKey
): boolean => {
  return resolveOrgKey(record?.orgKey) === orgKey;
};

export const userOrgScopedStorageKey = (
  baseKey: string,
  userId: string,
  orgKey: OrganizationKey
): string => `${baseKey}_${userId}_${orgKey}`;

/** Stable CRM doc id scoped to org (avoids cross-org prospect overwrites). */
export const orgProspectDocId = (orgKey: OrganizationKey, prospectId: string): string => {
  const base = String(prospectId || '').trim();
  if (!base) return `${orgKey}::unknown`;
  if (base.includes('::')) return base;
  return `${orgKey}::${base}`;
};

export const stripOrgProspectDocId = (scopedId: string): string => {
  const parts = String(scopedId || '').split('::');
  return parts.length > 1 ? parts.slice(1).join('::') : scopedId;
};

export const tagWithOrg = <T extends Record<string, any>>(
  record: T,
  orgKey: OrganizationKey
): T & { orgKey: OrganizationKey } => ({
  ...record,
  orgKey
});
